/**
 * Helpers for Data Collection label/entity extraction UX: whether a target
 * is still scanning a file, and whether mapped values sit outside the task
 * definition (the Select Label / Select Entity warning icon).
 */

/** True while columns are still being read or distinct labels are in flight. */
export function isLabelExtractionPending({ files, details } = {}) {
    if (details?.hasLabels === false) return false;
    const list = files || [];
    if (!list.length) return false;
    if (list.some((entry) => entry?.columnsLoading)) return true;
    return Boolean(details?.labelsLoading);
}

/**
 * Extracted values whose Replace mapping is not in the Business Goals catalog.
 * Dropped rows (`mapping === null`) are ignored — they no longer appear in
 * Select Label / Select Entity.
 */
export function unknownMappedLabels(details, availableLabels) {
    if (!details || details.hasLabels === false) return [];
    const available = new Set(availableLabels || []);
    const mappings = details.labelMappings || {};
    return (details.extractedLabels || []).filter((label) => {
        if (mappings[label] === null) return false;
        const mapped = mappings[label] ?? label;
        return !available.has(mapped);
    });
}

/**
 * Which upload targets currently show the unknown-label warning icon.
 * Shared details count as both files; split mode checks each form.
 */
export function unknownLabelSources({
    detailsByTarget,
    availableLabels,
    shareDetails,
    showDatasetCard = true,
    showBenchmarkCard = true,
    primaryDetailsTarget = 'dataset',
} = {}) {
    const hasUnknown = (target) =>
        unknownMappedLabels(detailsByTarget?.[target], availableLabels).length > 0;

    if (shareDetails && showDatasetCard && showBenchmarkCard) {
        return hasUnknown(primaryDetailsTarget) ? ['dataset', 'benchmark'] : [];
    }

    const sources = [];
    if (showDatasetCard && hasUnknown('dataset')) sources.push('dataset');
    if (showBenchmarkCard && hasUnknown('benchmark')) sources.push('benchmark');
    return sources;
}

/** Short confirmation copy for Next when unknown mapped labels/entities remain. */
export function unknownLabelsWarningCopy({ sources = [], isNer = false } = {}) {
    const noun = isNer ? 'entities' : 'labels';
    const title = isNer
        ? "Entities aren't in the task definition"
        : "Labels aren't in the task definition";
    if (!sources.length) {
        return { title, body: '' };
    }
    if (sources.length === 2) {
        return {
            title,
            body: `The dataset and benchmark include ${noun} that aren't defined for this task. Continue anyway?`,
        };
    }
    const file = sources[0] === 'benchmark' ? 'benchmark' : 'dataset';
    return {
        title,
        body: `This ${file} includes ${noun} that aren't defined for this task. Continue anyway?`,
    };
}
